import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, Sparkles } from 'lucide-react';
import { InnerLayout, InnerLayoutSection } from '../components/layout/InnerLayout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CreateOrgModal } from '../components/layout/status/CreateOrgModal';
import { useHomeData } from './home/hooks/useHomeData';
import { HomeMetricsOverview } from './home/components/HomeMetricsOverview';
import { OrganizationCard } from './home/components/OrganizationCard';
import { ChangePlanModal } from './home/components/ChangePlanModal';

export const HomeView: React.FC = () => {
  const navigate = useNavigate();
  const {
    user,
    organizations,
    quotasByOrg,
    totalProjects,
    totalDatastores,
    totalSubmissionsThisMonth,
    totalStorageUsed,
    createOrgOpen,
    setCreateOrgOpen,
    upgradeModalOpen,
    setUpgradeModalOpen,
    selectedOrgIdForUpgrade,
    setSelectedOrgIdForUpgrade,
    selectedTier,
    setSelectedTier,
    isUpgrading,
    openUpgradeModal,
    handleUpgradeSubscription,
    canUserAdminOrg,
    currentSelectedOrgForModal,
    switchOrganization,
  } = useHomeData();

  return (
    <InnerLayout
      header={{
        title: 'Home',
        description: `Welcome back, ${
          user?.full_name || 'there'
        }. Select an organization below to access your projects and forms, or create a new workspace.`,
        actions: [
          {
            label: 'Create Organization',
            icon: Plus,
            variant: 'primary',
            onClick: () => setCreateOrgOpen(true),
          },
        ],
      }}
    >
      <div className="space-y-7">
        {/* 1. True Aggregated Global Metrics */}
        <HomeMetricsOverview
          organizationsCount={organizations.length}
          totalProjects={totalProjects}
          totalDatastores={totalDatastores}
          totalSubmissionsThisMonth={totalSubmissionsThisMonth}
          totalStorageUsed={totalStorageUsed}
        />

        {/* 2. Organizations Workspaces Hub */}
        <InnerLayoutSection
          title="Your Organizations"
          icon={Building2}
          description="Click any organization to open its projects, datastores, and team members."
          actions={[
            {
              label: 'New Organization',
              icon: Plus,
              variant: 'ghost',
              className: 'text-cf-orange hover:text-cf-orangeHover',
              onClick: () => setCreateOrgOpen(true),
            },
          ]}
        >
          {organizations.length === 0 ? (
            <div className="p-8 rounded-xl bg-cf-surface border border-dashed border-cf-border text-center space-y-3">
              <div className="w-12 h-12 mx-auto rounded-full bg-cf-surfaceActive flex items-center justify-center text-cf-textMuted">
                <Building2 size={24} />
              </div>
              <div className="max-w-md mx-auto">
                <h3 className="text-sm font-semibold text-cf-textPrimary">No organizations yet</h3>
                <p className="text-xs text-cf-textSecondary mt-1">
                  Create your first organization to organize your forms, team members, and data
                  pipelines.
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Plus size={13} />}
                onClick={() => setCreateOrgOpen(true)}
              >
                Create Organization
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {organizations.map((org) => {
                const canManage = canUserAdminOrg(org.role);
                const orgQuota = quotasByOrg[org.id];

                return (
                  <OrganizationCard
                    key={org.id}
                    org={org}
                    quota={orgQuota}
                    canManage={canManage}
                    onSelect={() => {
                      switchOrganization(org);
                      navigate(`/organization/${org.id}`);
                    }}
                    onOpenUpgrade={() => openUpgradeModal(org.id)}
                  />
                );
              })}

              {/* Add New Organization Card */}
              <Card
                onClick={() => setCreateOrgOpen(true)}
                className="p-5 border-dashed border-cf-border hover:border-cf-orange/50 hover:bg-cf-surface/50 transition-all flex flex-col items-center justify-center text-center space-y-2 min-h-[140px] cursor-pointer group shadow-none"
              >
                <div className="w-9 h-9 rounded-full bg-cf-surfaceActive group-hover:bg-cf-orange/10 flex items-center justify-center text-cf-textMuted group-hover:text-cf-orange transition-colors">
                  <Plus size={16} />
                </div>
                <div>
                  <span className="text-xs font-semibold text-cf-textPrimary group-hover:text-cf-orange block">
                    Create New Organization
                  </span>
                  <span className="text-xs text-cf-textMuted mt-0.5 block">
                    Spin up an isolated workspace for a new team or client
                  </span>
                </div>
              </Card>
            </div>
          )}
        </InnerLayoutSection>

        {/* 3. Getting Started & Platform Guide */}
        <div className="p-5 rounded-xl bg-cf-surface border border-cf-border space-y-4 shadow-xs">
          <div className="flex items-center gap-2 text-cf-textPrimary">
            <Sparkles size={16} className="text-cf-orange" />
            <h3 className="text-xs font-semibold uppercase tracking-wider">How Ericius Works</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
            <div className="p-3.5 rounded-lg bg-cf-bg border border-cf-border space-y-1.5">
              <div className="flex items-center gap-2 text-cf-textPrimary font-semibold">
                <span className="w-5 h-5 rounded-full bg-cf-surfaceActive flex items-center justify-center text-xs font-bold">
                  1
                </span>
                <span>Workspaces &amp; Projects</span>
              </div>
              <p className="text-cf-textSecondary leading-relaxed">
                Group your ingestion endpoints by business, team, or client to ensure strict
                multi-tenant data separation and clear permissions.
              </p>
            </div>

            <div className="p-3.5 rounded-lg bg-cf-bg border border-cf-border space-y-1.5">
              <div className="flex items-center gap-2 text-cf-textPrimary font-semibold">
                <span className="w-5 h-5 rounded-full bg-cf-surfaceActive flex items-center justify-center text-xs font-bold">
                  2
                </span>
                <span>Forms &amp; Datastores</span>
              </div>
              <p className="text-cf-textSecondary leading-relaxed">
                Create datastores for contact forms, waitlists, surveys, or webhooks. Every
                datastore gives you an instant, secure submission URL.
              </p>
            </div>

            <div className="p-3.5 rounded-lg bg-cf-bg border border-cf-border space-y-1.5">
              <div className="flex items-center gap-2 text-cf-textPrimary font-semibold">
                <span className="w-5 h-5 rounded-full bg-cf-surfaceActive flex items-center justify-center text-xs font-bold">
                  3
                </span>
                <span>Instant Notifications</span>
              </div>
              <p className="text-cf-textSecondary leading-relaxed">
                Connect your own email provider (SMTP, Resend) or webhooks to automatically notify
                your team when a new submission is received.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <CreateOrgModal
        isOpen={createOrgOpen}
        onClose={() => setCreateOrgOpen(false)}
      />

      <ChangePlanModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        organizations={organizations}
        selectedOrgId={selectedOrgIdForUpgrade}
        onSelectOrgId={setSelectedOrgIdForUpgrade}
        selectedTier={selectedTier}
        onSelectTier={setSelectedTier}
        isUpgrading={isUpgrading}
        currentSelectedOrg={currentSelectedOrgForModal}
        onConfirmUpgrade={handleUpgradeSubscription}
      />
    </InnerLayout>
  );
};
